import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  FaDownload,
  FaEye,
  FaFileInvoice,
  FaFilePdf,
  FaPencilAlt,
} from "react-icons/fa";
import { useQuery } from "react-query";
import { BeatLoader } from "react-spinners";
import {
  exportPartnerAnnexe,
  getInvoiceFiles,
  getPrestationAnnexe,
} from "../../../api";
import _ from "../../../i18n";
import {
  formatAmount,
  getMonthShortLabelObject,
  renderDurationDisplay,
} from "../../../utils";
import { IconClose } from "../../common/Icons";
import InvoiceDownloader from "./InvoiceModal";
import Preview from "./Preview";
import TSCard from "./components/TSCard/TSCard";
import { toast } from "react-toastify";
import { Button, Modal, Radio } from "antd";

export default function InvoiceTracking({
  collaboratorOptions,
  organization,
  token,
  lng,
  year,
  onClose,
}) {
  const today = new Date();
  const currentMonth_1 = today.getMonth();
  const previousMonth = currentMonth_1 === 0 ? 11 : currentMonth_1 - 1;

  const activeMonthRef = useRef(null);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [openPartnerAnnexeModal, setOpenPartnerAnnexeModal] = useState(false);
  const [displayPDF, setDisplayPDF] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAnnexe, setDownloadingAnnexe] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");

  const monthsArray = getMonthShortLabelObject(lng);
  const previousMonthData = monthsArray[previousMonth];

  const [filter, setFilter] = useState({
    tabs: monthsArray,
    period: previousMonthData,
    start: previousMonthData.start,
    end: previousMonthData.end,
  });

  const [isLastWeekOfMonth, setIsLastWeekOfMonth] = useState(false);
  const [annexe, setAnnexe] = useState(null);

  useEffect(() => {
    const today = new Date();
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );
    const lastWeekStart = new Date(lastDayOfMonth);
    lastWeekStart.setDate(lastDayOfMonth.getDate() - 6);

    setIsLastWeekOfMonth(today >= lastWeekStart);
  }, []);

  useEffect(() => {
    if (activeMonthRef.current) {
      activeMonthRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [filter.period?.id]);

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const invoiceYear = parseInt(year, 10);
  const invoiceMonth = parseInt(filter.period.id, 10);

  const shouldShowDownload =
    invoiceYear < currentYear ||
    invoiceMonth < currentMonth ||
    (invoiceMonth === currentMonth && isLastWeekOfMonth);

  const handleMonthChange = (tab) => {
    setFilter({
      ...filter,
      period: tab,
      start: tab.start,
      end: tab.end,
    });
  };

  let getInvoicesQuery = useQuery(
    ["getInvoicesQuery", token, organization, year],
    async () => {
      if ((token, organization, year)) {
        try {
          const response = await getInvoiceFiles(token, organization.id, year);

          const data = response.data.data;
          setInvoices(data.invoices);
          return data;
        } catch (e) {
          console.log(e);
          setInvoices([]);
          return null;
        }
      }
    },
    {
      enabled: !!token && !!organization && !!year,
    }
  );

  let getInvoiceAnnexeQuery = useQuery(
    ["getInvoiceAnnexeQuery", token, organization],
    async () => {
      if (token && organization) {
        const startDate = year + "-" + filter.start + "T00:00:00";
        const endDate = year + "-" + filter.end + "T00:00:00";
        const ids = collaboratorOptions.map((item) => item.value);
        const slice_1 = ids.slice(0, Math.ceil(ids.length / 2));
        const slice_2 = ids.slice(Math.ceil(ids.length / 2), ids.length);

        try {
          // Execute both requests in parallel
          const [response_1, response_2] = await Promise.all([
            getPrestationAnnexe(
              token,
              organization.id,
              startDate,
              endDate,
              slice_1
            ),
            getPrestationAnnexe(
              token,
              organization.id,
              startDate,
              endDate,
              slice_2
            ),
          ]);

          // Merge data from both responses
          const data_1 = response_1.data.data;
          const data_2 = response_2.data.data;

          const mergedRows = [...data_1.rows, ...data_2.rows];

          const mergedTotal = Object.keys(data_1.total).reduce((acc, key) => {
            acc[key] = (data_1.total[key] || 0) + (data_2.total[key] || 0);
            return acc;
          }, {});

          setAnnexe({
            rows: mergedRows,
            total: mergedTotal,
          });
        } catch (e) {
          console.log(e);
          setAnnexe(null);
          throw new Error(e.message);
        }
      }
    },
    {
      enabled: !!token && !!organization && !!year,
    }
  );

  useEffect(() => {
    getInvoiceAnnexeQuery.refetch();
  }, [filter.period]);

  const printPartnerAnnexe = async () => {
    setDownloadingAnnexe(true);
    const ids = collaboratorOptions.map((item) => item.value);
    if (token && organization) {
      const startDate = year + "-" + filter.start + "T00:00:00";
      const endDate = year + "-" + filter.end + "T00:00:00";
      try {
        await exportPartnerAnnexe(
          token,
          organization.id,
          ids,
          selectedPeriod,
          year,
          startDate,
          endDate
        ).then((resp) => {
          toast.success(_("Téléchargement est terminé avec succès."));
        });
        setDownloadingAnnexe(false);
        setOpenPartnerAnnexeModal(false);
      } catch (e) {
        setDownloadingAnnexe(false);
        toast.error(_(e));
      }
    }
  };

  const renderFilterFrame = () => {
    return (
      <div className="filter-frame" style={{ padding: 0 }}>
        <div
          className="additional-line"
          style={{
            overflowX: "auto",
            height: "100%",
          }}
        >
          {filter.tabs.map((tab, index) => {
            const isActive = filter.period.id === tab.id;
            return (
              <div
                key={`${filter.period.id}-${index}`}
                ref={isActive ? activeMonthRef : null}
                className={`tag ${isActive ? "active" : ""} ${
                  filter.period &&
                  moment().year() == year &&
                  tab.id > moment().month() + 1 &&
                  "disabled"
                }`}
                onClick={() =>
                  filter.period &&
                  moment().year() == year &&
                  tab.id > moment().month() + 1
                    ? null
                    : handleMonthChange(tab)
                }
              >
                {tab.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const {
    period: { id: month },
  } = filter;

  const fileName =
    invoices[year] &&
    invoices[year][month] &&
    invoices[year][month][0].fileName;
  const fileUrl =
    invoices[year] && invoices[year][month] && invoices[year][month][0].url;

  return (
    <Fragment>
      <div
        style={{
          position: "absolute",
          backgroundColor: "white",
          width: 900,
          height: "calc(100% - 40px)",
          right: 0,
          overflow: "hidden",
        }}
      >
        {/* Modal Prestation */}
        <InvoiceDownloader
          token={token}
          navCommunity={organization}
          data={collaboratorOptions}
          open={open}
          loading={downloading}
          filter={filter}
          year={year}
          close={() => setOpen(false)}
          refresh={() => getInvoicesQuery.refetch()}
        />

        {/* Modal Partner Annexe */}
        <Modal
          title={<p>{_("exportPartnerAnnexe")}</p>}
          footer={
            <Button className="download-button" onClick={printPartnerAnnexe}>
              {downloadingAnnexe ? (
                <BeatLoader color="#3498db" size={10} />
              ) : (
                _("confirm")
              )}
            </Button>
          }
          width={700}
          loading={downloadingAnnexe}
          open={openPartnerAnnexeModal}
          onCancel={() => setOpenPartnerAnnexeModal(false)}
        >
          <div className="form-control">
            <div className="field-control">
              <label className="period-label">{_("selectPeriod")}</label>
              <div className="radio-group">
                <Radio.Group
                  block
                  options={[
                    { label: _("monthly"), value: "monthly" },
                    { label: _("annual"), value: "annual" },
                  ]}
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                />
              </div>
            </div>

            <div>Mensuel: *{filter.period.label}</div>
          </div>
        </Modal>

        <div className="sidebar_header" style={{ height: 50 }}>
          <div className="sidebar_header_infos">
            <h3>{_("accountingPrestation")}</h3>
          </div>
          <div className="sidebar_header_action" onClick={onClose}>
            <IconClose size={12} />
          </div>
        </div>

        {/* filters */}
        {renderFilterFrame()}

        {/* body content */}
        <div
          className="sidebar_body_content"
          style={{ height: "calc(100% - 250px)", padding: "20px 25px" }}
        >
          <div>
            {getInvoiceAnnexeQuery.isFetching ? (
              <div className="ripple_loading">
                <img src={"/img/Ripple-1s-150px.svg"} alt="" />
              </div>
            ) : (
              annexe && (
                <Fragment>
                  <div className="invoice-total">
                    <h3>{_("totalSummary")}</h3>
                    <div className="summary-rows">
                      {Object.entries(annexe.total).map(([key, value]) => (
                        <div key={key}>
                          <label>{_(key)}</label>
                          <span>
                            {key == "volumetry"
                              ? value
                              : ["real", "theoric"].includes(key)
                              ? renderDurationDisplay(value)
                              : formatAmount(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="invoice-container">
                    {annexe.rows.map((row, index) => (
                      <TSCard
                        key={`${row.id}-${index}`}
                        collaboratorId={row.id}
                        name={row.name}
                        grade={row.grade}
                        timesheet={{
                          real: row.real,
                          theoric: row.theoric,
                          volumetry: row.clients.volumetry,
                          fee: row.adjustment,
                        }}
                        lng={lng}
                      />
                    ))}
                  </div>
                </Fragment>
              )
            )}
          </div>
        </div>

        {/* Attachments */}
        <div className="sidebar_footer" style={{ height: 120 }}>
          <h3>Attachements</h3>
          <div style={{ display: "flex", gap: 20 }}>
            <div className="print_invoice">
              <div>
                <FaFilePdf color="#de4848" size={24} />
                <div>
                  <strong>{`Facture M${filter.period.end.slice(0, 2)}-${year
                    .toString()
                    .slice(2, 4)}`}</strong>
                  <span>{filter.period.label}</span>
                </div>
              </div>

              {invoices[year] &&
              Object.keys(invoices[year]).includes(String(month)) &&
              fileName?.length ? (
                <div style={{ display: "flex" }}>
                  <button onClick={() => setDisplayPDF(true)}>
                    <div className="preview_invoice_btn">
                      <FaEye />
                    </div>
                  </button>
                  <button onClick={() => setOpen(true)}>
                    <div className="edit_invoice_btn">
                      <FaPencilAlt />
                    </div>
                  </button>
                </div>
              ) : (
                <button
                  onClick={shouldShowDownload ? () => setOpen(true) : null}
                  style={{
                    color: shouldShowDownload ? "#18a0fb" : "#6d7f9280",
                    cursor: shouldShowDownload ? "pointer" : "not-allowed",
                  }}
                >
                  {downloading ? (
                    <BeatLoader color="#3498db" size={10} />
                  ) : (
                    <div className="export_invoice_btn">
                      <FaDownload />
                    </div>
                  )}
                </button>
              )}
            </div>

            <div className="print_invoice">
              <div>
                <FaFilePdf color="#de4848" size={24} />
                <div>
                  <strong style={{ margin: 0 }}>Annexe Partenaires</strong>
                </div>
              </div>

              <button
                onClick={() => setOpenPartnerAnnexeModal(true)}
                style={{
                  pointerEvents: downloadingAnnexe ? "none" : "all",
                  opacity: downloadingAnnexe ? 0.75 : 1,
                }}
              >
                {downloadingAnnexe ? (
                  <BeatLoader color="#3498db" size={10} />
                ) : (
                  <div className="export_invoice_btn">
                    <FaDownload />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {fileName?.length && displayPDF && (
        <Preview src={fileUrl} onClose={() => setDisplayPDF(false)} />
      )}
    </Fragment>
  );
}
